import { Fragment, memo } from 'react';

import {
  DATE_FORMAT_LONG_MINUTE_FNS,
  DATE_FORMAT_LONG_MINUTE_SHORT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmPreview } from 'APIPackage/KnmSearchModule/interfaces/IKnmPreview';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';

import styles from './KnmPreview.module.scss';
import { convertArrayToDate } from '../common/convertArrayToDate';

interface IKnmResultProps {
  passportData: IKnmPreview;
}

export const KnmResult = memo(({ passportData }: IKnmResultProps) => (
  <div className={styles.ColumnContent}>
    {!!passportData.objects?.length &&
      passportData.objects.map((obj, index) => (
        <Fragment key={index}>
          <span className={styles.SectionTitle}>{`Объект проведения КНМ №${index + 1}`}</span>
          <div className={styles.Row}>
            <div className={styles.ColText}>Адрес объекта проведения КНМ</div>
            <div className={styles.ColValue}>{obj.address}</div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Тип адреса места составления акта о проведении КНМ</div>
            <div className={styles.ColValue}>{obj.addressTypeName}</div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Тип объекта проведения КНМ</div>
            <div className={styles.ColValue}>{obj.typeName}</div>
          </div>
          {obj.riskName && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Категория риска</div>
              <div className={styles.ColValue}>{obj.riskName}</div>
            </div>
          )}

          {!!obj.chlistHeaders?.length && (
            <>
              <span className={styles.SectionTitle}>Проверочные листы</span>
              {obj.chlistHeaders.map((chlistHeader, chlistHeaderIndex) => (
                <Fragment key={chlistHeaderIndex}>
                  <span className={styles.SectionTitle}>{`Проверочный лист №${chlistHeaderIndex + 1}`}</span>
                  {chlistHeader.name && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Наименование проверочного листа</div>
                      <div className={styles.ColValue}>{chlistHeader.name}</div>
                    </div>
                  )}
                  {chlistHeader.approval && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Сведения об утверждении проверочного листа</div>
                      <div className={styles.ColValue}>{chlistHeader.approval}</div>
                    </div>
                  )}
                  {chlistHeader.frguOrgName && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>
                        Наименование органа контроля (надзора) из Федерального реестра государственных и муниципальных
                        услуг (ФРГУ)
                      </div>
                      <div className={styles.ColValue}>{chlistHeader.frguOrgName}</div>
                    </div>
                  )}
                  {!!chlistHeader.chlist?.length && (
                    <>
                      <span className={styles.SectionTitle}>Список вопросов проверочного листа</span>
                      {chlistHeader.chlist
                        .sort((a, b) => b.posId - a.posId)
                        .map((chlist, chlistIndex) => (
                          <Fragment key={chlistIndex}>
                            <span className={styles.SectionTitle}>{`Контрольный вопрос №${chlistIndex + 1}`}</span>
                            {chlist.posId && (
                              <div className={styles.Row}>
                                <div className={styles.ColText}>Идентификатор вопроса проверочного листа</div>
                                <div className={styles.ColValue}>{chlist.posId}</div>
                              </div>
                            )}
                            {chlist.question && (
                              <div className={styles.Row}>
                                <div className={styles.ColText}>Вопрос проверочного листа</div>
                                <div className={styles.ColValue}>{chlist.question}</div>
                              </div>
                            )}
                            {chlist.answerTypeName && (
                              <div className={styles.Row}>
                                <div className={styles.ColText}>Ответ проверочного листа</div>
                                <div className={styles.ColValue}>{chlist.answerTypeName}</div>
                              </div>
                            )}
                            {chlist.answerText && (
                              <div className={styles.Row}>
                                <div className={styles.ColText}>Ответ (иное) проверочного листа</div>
                                <div className={styles.ColValue}>{chlist.answerText}</div>
                              </div>
                            )}
                            {chlist.props && (
                              <div className={styles.Row}>
                                <div className={styles.ColText}>
                                  Реквизиты НПА устанавливающего обязательные требования
                                </div>
                                <div className={styles.ColValue}>{chlist.props}</div>
                              </div>
                            )}
                          </Fragment>
                        ))}
                    </>
                  )}
                </Fragment>
              ))}
            </>
          )}
          {passportData.objects?.[0]?.supervisions?.[index] && (
            <>
              <span className={styles.SectionTitle}>Вид государственного контроля (надзора)</span>
              <div className={styles.Row}>
                <div className={styles.ColText}>Вид государственного контроля (надзора)</div>
                <div className={styles.ColValue}>{passportData.objects?.[0]?.supervisions?.[index]?.name}</div>
              </div>
            </>
          )}
          {obj.result && (
            <>
              <span className={styles.SectionTitle}>Результат проведения КНМ</span>
              {obj.result.actCreateDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата и время составления акта о проведении КНМ</div>
                  <div className={styles.ColValue}>
                    {formatDate({
                      date: convertArrayToDate(obj.result.actCreateDate),
                      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                      parseFormat: DATE_FORMAT_LONG_MINUTE_SHORT_FNS,
                    })}
                  </div>
                </div>
              )}
              {obj.result.actAddress && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Место составления акта о проведении КНМ</div>
                  <div className={styles.ColValue}>{obj.result.actAddress}</div>
                </div>
              )}
              {obj.result.actAddressTypeName && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип адреса места составления акта о проведении КНМ</div>
                  <div className={styles.ColValue}>{obj.result.actAddressTypeName}</div>
                </div>
              )}
              {obj.result.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала проведения КНМ (фактическая)</div>
                  <div className={styles.ColValue}>
                    {formatDate({
                      date: convertArrayToDate(obj.result.startDate),
                      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                      parseFormat: DATE_FORMAT_LONG_MINUTE_SHORT_FNS,
                    })}
                  </div>
                </div>
              )}
              {obj.result.durationDays && obj.result.durationDays > 0 && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Длительность КНМ (в днях)</div>
                  <div className={styles.ColValue}>{obj.result.durationDays}</div>
                </div>
              )}
              {obj.result.durationHours && obj.result.durationHours > 0 && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Длительность КНМ (в часах)</div>
                  <div className={styles.ColValue}>{obj.result.durationHours}</div>
                </div>
              )}
              {obj.result.representativeFullName && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>
                    Фамилия, имя, отчество (последнее – при наличии) руководителя, иного должностного лица юридического
                    лица, уполномоченного представителя юридического лица, уполномоченного представителя индивидуального
                    предпринимателя, присутствовавших при проведении КНМ
                  </div>
                  <div className={styles.ColValue}>{obj.result.representativeFullName}</div>
                </div>
              )}
              {obj.result.representativePosition && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>
                    Должность руководителя, иного должностного лица юридического лица, уполномоченного представителя
                    юридического лица, уполномоченного представителя индивидуального предпринимателя, присутствовавших
                    при проведении КНМ
                  </div>
                  <div className={styles.ColValue}>{obj.result.representativePosition}</div>
                </div>
              )}
              {obj.result.representativeTypeName && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип представителя проверяемого лица</div>
                  <div className={styles.ColValue}>{obj.result.representativeTypeName}</div>
                </div>
              )}
              {obj.result.resultInformation
                ?.filter(item => item.typeCode === 'ACT_WAS_NOT_READ_NOTE')
                .map((item, objIndex) => {
                  return (
                    <div key={objIndex} className={styles.Row}>
                      <div className={styles.ColText}>{item.typeName}</div>
                      <div className={styles.ColValue}>{item.text}</div>
                    </div>
                  );
                })}
              {obj.result.actWasNotRead === false && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>
                    Отметка об ознакомлении или отказе от ознакомления с актом КНМ руководителя, иного должностного лица
                    или уполномоченного представителя юридического лица, индивидуального предпринимателя, его
                    уполномоченного представителя, присутствовавших при проведении КНМ, о наличии их подписей или об
                    отказе от совершения подписи
                  </div>
                  <div className={styles.ColValue}>0</div>
                </div>
              )}
              {obj.result.wrongDataReasonSec && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>
                    Сведения о несоответствии информации, содержащейся в уведомлении о начале осуществления отдельных
                    видов предпринимательской деятельности, обязательным требованиям (с указанием положений нормативных
                    правовых актов)
                  </div>
                  <div className={styles.ColValue}>{obj.result.wrongDataReasonSec}</div>
                </div>
              )}
              {obj.result.absentViolationNote && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>
                    Указание на отсутствие выявленных нарушений обязательных требований или требований, установленных
                    муниципальными правовыми актами (в случае если нарушений обязательных требований или требований,
                    установленных муниципальными правовыми актами, не выявлено)
                  </div>
                  <div className={styles.ColValue}>{obj.result.absentViolationNote}</div>
                </div>
              )}
              {obj.result.unimpossibleReason && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>
                    Сведения о причинах невозможности проведения КНМ (в случае если КНМ не проведено)
                  </div>
                  <div className={styles.ColValue}>{obj.result.unimpossibleReason}</div>
                </div>
              )}
              {obj.result.undoigSec && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>
                    Сведения об отмене результатов КНМ в случае, если такая отмена была произведена
                  </div>
                  <div className={styles.ColValue}>{obj.result.undoigSec}</div>
                </div>
              )}

              {!!obj.result.resultInformation?.length && (
                <>
                  <span className={styles.SectionTitle}>Сведения о результате КНМ</span>
                  {obj.result.resultInformation.map((res, resIndex) => (
                    <Fragment key={resIndex}>
                      <span className={styles.SectionTitle}>{`Сведения о результате №${resIndex + 1}`}</span>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Тип сведений о результате</div>
                        <div className={styles.ColValue}>{res.typeName}</div>
                      </div>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Формулировка сведения о результате</div>
                        <div className={styles.ColValue}>{res.text}</div>
                      </div>
                    </Fragment>
                  ))}
                </>
              )}
              {!!obj.result.resultInspector?.length && (
                <>
                  <span className={styles.SectionTitle}>Список должностных лиц, проводивших КНМ</span>
                  {obj.result.resultInspector.map((inspector, inspectorIndex) => (
                    <Fragment key={inspectorIndex}>
                      <span className={styles.SectionTitle}>
                        {`Фамилия, имя, отчество (последнее - при наличии) и должность должностного лица (должностных лиц), проводившего КНМ №${
                          inspectorIndex + 1
                        }`}
                      </span>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>ФИО должностного лица, уполномоченного на проведение КНМ</div>
                        <div className={styles.ColValue}>{inspector.fullName}</div>
                      </div>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>
                          Должность должностного лица, уполномоченного на проведение КНМ
                        </div>
                        <div className={styles.ColValue}>{inspector.position}</div>
                      </div>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Тип должностного лица, уполномоченного на проведение КНМ</div>
                        <div className={styles.ColValue}>{inspector.inspectorTypeName}</div>
                      </div>
                    </Fragment>
                  ))}
                </>
              )}
              {!!obj.result.resultRepresentative?.length && (
                <>
                  <span className={styles.SectionTitle}>Должностное лицо, присутствовавшее при проведении КНМ</span>
                  {obj.result.resultRepresentative.map((rep, repIndex) => (
                    <Fragment key={repIndex}>
                      <span className={styles.SectionTitle}>
                        {`Должностное лицо, присутствовавшее при проведении КНМ №${repIndex + 1}`}
                      </span>
                      {rep.fullName && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>
                            Фамилия, имя, отчество (последнее - при наличии) и должность руководителя, иного
                            должностного лица юридического лица, уполномоченного представителя юридического лица,
                            уполномоченного представителя индивидуального предпринимателя, присутствовавших при
                            проведении КНМ
                          </div>
                          <div className={styles.ColValue}>{rep.fullName}</div>
                        </div>
                      )}
                      {rep.position && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>
                            Должность руководителя, иного должностного лица юридического лица, уполномоченного
                            представителя юридического лица, уполномоченного представителя индивидуального
                            предпринимателя, присутствовавших при проведении КНМ
                          </div>
                          <div className={styles.ColValue}>{rep.position}</div>
                        </div>
                      )}
                      {rep.typeName && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>
                            Тип представителя должностного лица, уполномоченного представителя юридического лица,
                            уполномоченного представителя индивидуального предпринимателя, присутствовавших при
                            проведении КНМ
                          </div>
                          <div className={styles.ColValue}>{rep.typeName}</div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </>
              )}
            </>
          )}
        </Fragment>
      ))}
  </div>
));
